/**
 * El recibo de una venta en HOJA CARTA, con la plantilla de Softfi (2026-09-29).
 *
 * El dueño mostró como modelo un recibo en hoja carta de otro sistema: el logo
 * arriba, los datos del cliente en un recuadro, la tabla con Cantidad,
 * Descripción, SKU, Precio y Total, los totales en su recuadro y «DOCUMENTO
 * NO FISCAL» abajo. Antes «Hoja carta» imprimía el ticket del rollo estirado
 * en una hoja.
 *
 * ⚠️ Los montos son los mismos que el ticket (`ticketEscpos.componerTicket`):
 * los del documento congelado, sin recalcular nada, y en la moneda elegida
 * con la tasa DEL DÍA DE LA VENTA. Si se cambia una cuenta allá, se cambia
 * aquí.
 */

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { HojaCarta, domicilioFiscalDe, ROJO, TENUE, imprimirHojaCarta, type TotalDeHoja } from "@/components/HojaCarta";
import { useSucursales } from "@/components/SucursalActual";
import { monedas as endpointMonedas, negocio } from "@/lib/endpoints";
import { hojasCartaComoPdf } from "@/lib/hojaCartaPdf";
import { conImpuestosDeLaVenta, llevaMarcaDeExento, nombresDeLosExentos } from "@/lib/exentos";
import { comoFormato, formatMoney, formatRate, toNumber, type FormatoMoneda, monedaImpresa } from "@/lib/money";
import type { Sale } from "@/lib/types";
import { contactoDelPapel } from "./ticketEscpos";

const ID_DEL_PAPEL = "recibo-en-carta";

/** Imprime el recibo en hoja carta que está en pantalla (`ReciboEnCarta`). */
export function imprimirReciboEnCarta(): boolean {
  return imprimirHojaCarta(ID_DEL_PAPEL);
}

/** El recibo en hoja carta que está en pantalla, como PDF para mandar. */
export function reciboEnCartaComoPdf(): Blob | null {
  return hojasCartaComoPdf(ID_DEL_PAPEL);
}

/** «2» en vez de «2.000», «1,5» en vez de «1.500». */
export function cantidadCorta(qty: string): string {
  const n = toNumber(qty);
  return Number.isInteger(n) ? String(n) : n.toLocaleString("es-VE", { maximumFractionDigits: 3 });
}

export function fechaDelPapel(iso: string): string {
  return new Date(iso).toLocaleString("es-VE", { dateStyle: "short", timeStyle: "short" });
}

/**
 * La hoja, para la vista previa de la ventana del recibo cuando se elige
 * «Hoja carta». Se imprime con `imprimirReciboEnCarta`.
 */
export function ReciboEnCarta({
  venta,
  moneda,
  codigoDeProducto = null,
}: {
  venta: Sale;
  /** La moneda elegida para el papel, con la tasa del día de la venta. */
  moneda: { codigo: string; tasa: number } | null;
  /**
   * Qué código va en la columna de cada producto (2026-10-07): el SKU, el de
   * barras, o ninguno (sin la columna). Sin elegir, el de siempre: el SKU o,
   * si no tiene, el de barras.
   */
  codigoDeProducto?: "sku" | "barcode" | "ninguno" | null;
}) {
  const { data: datosNegocio } = useQuery({ queryKey: ["negocio"], queryFn: negocio.obtener });
  const { data: listaMonedas } = useQuery({ queryKey: ["monedas"], queryFn: endpointMonedas.listar });
  const { varias } = useSucursales();

  const otra = moneda && moneda.codigo !== venta.currency_code && moneda.tasa > 0 ? moneda : null;
  const codigo = otra?.codigo ?? venta.currency_code;
  const formato: FormatoMoneda = (listaMonedas ?? []).map(comoFormato).find((m) => m.code === codigo) ?? {
    code: codigo,
    symbol: codigo,
    decimals: 2,
    symbol_before: false,
  };
  const convertir = (valor: string) => (otra ? toNumber(valor) * otra.tasa : toNumber(valor));
  const importe = (valor: string) => formatMoney(convertir(valor), formato, { sinSimbolo: true });

  // ── Las líneas ─────────────────────────────────────────────────────────────
  const impuestosDePedido = venta.impuestos_de_pedido ?? [];
  const cobrados = impuestosDePedido.map((x) => x.tax_id);
  const exentosDe = nombresDeLosExentos(venta.items, impuestosDePedido);

  const conColumnaDeCodigo = codigoDeProducto !== "ninguno";
  const filas = venta.items.map((l) => {
    const conIva = conImpuestosDeLaVenta(toNumber(l.line_total), l.exento_de, impuestosDePedido);
    const detalle: string[] = [];
    if (conIva !== null) detalle.push(`Con IVA: ${importe(String(conIva))}`);
    if (toNumber(l.discount) > 0) detalle.push(`Descuento: −${importe(l.discount)}`);
    if (l.staff_name) detalle.push(`Atendió: ${l.staff_name}`);
    const descripcion: ReactNode = (
      <>
        {l.name}
        {l.variant_name && <span style={{ color: TENUE }}> · {l.variant_name}</span>}
        {llevaMarcaDeExento(l.exento_de, cobrados) && " (E)"}
        {detalle.length > 0 && (
          <div style={{ color: TENUE, fontSize: "10.5px" }}>{detalle.join(" · ")}</div>
        )}
      </>
    );
    return {
      id: l.id,
      celdas: [
        <strong key="c">{cantidadCorta(l.qty)}</strong>,
        descripcion,
        ...(conColumnaDeCodigo
          ? [(codigoDeProducto === "sku" ? l.sku : codigoDeProducto === "barcode" ? l.barcode : l.codigo) ?? "—"]
          : []),
        importe(l.unit_price),
        <strong key="t">{importe(l.line_total)}</strong>,
      ],
    };
  });

  // ── La cuenta, como en el ticket ───────────────────────────────────────────
  const hayDescuento = toNumber(venta.discount_total) > 0;
  const hayImpuesto = toNumber(venta.tax_total) > 0;
  const hayRedondeo = toNumber(venta.rounding_adjustment) !== 0;
  const sumaDePedido = impuestosDePedido.reduce((acc, x) => acc + toNumber(x.amount), 0);
  const impuestoDeProductos = toNumber(venta.tax_total) - sumaDePedido;

  const totales: TotalDeHoja[] = [];
  if (hayDescuento || hayImpuesto || hayRedondeo) {
    totales.push({ etiqueta: `Subtotal ${monedaImpresa(codigo)}`, valor: importe(venta.subtotal) });
  }
  if (hayDescuento) {
    totales.push({
      etiqueta: venta.discount_code_text ? `Descuento (${venta.discount_code_text})` : "Descuento",
      valor: `−${importe(venta.discount_total)}`,
    });
  }
  if (impuestoDeProductos > 0) {
    totales.push({ etiqueta: "Impuesto", valor: importe(String(impuestoDeProductos)) });
  }
  for (const x of impuestosDePedido) {
    if (exentosDe.includes(x.name)) {
      totales.push({ etiqueta: `Base ${x.name}`, valor: importe(x.base_amount) });
    }
    totales.push({ etiqueta: `${x.name} ${x.rate_percent}%`, valor: importe(x.amount) });
  }
  if (hayRedondeo) totales.push({ etiqueta: "Redondeo", valor: importe(venta.rounding_adjustment) });
  totales.push({ etiqueta: `Total ${monedaImpresa(codigo)}`, valor: importe(venta.total), fuerte: true });
  if (otra) {
    totales.push({ etiqueta: "Tasa", valor: `1 ${monedaImpresa(venta.currency_code)} = ${formatRate(String(otra.tasa))}` });
    totales.push({
      etiqueta: "Equivale a",
      valor: `${formatMoney(venta.total, (listaMonedas ?? []).map(comoFormato).find((m) => m.code === venta.currency_code) ?? formato, { sinSimbolo: true })} ${monedaImpresa(venta.currency_code)}`,
    });
  }

  const { direccion, telefono } = contactoDelPapel(venta, datosNegocio);
  const esVenezuela = datosNegocio?.country_code === "VE";

  return (
      <HojaCarta
        id={ID_DEL_PAPEL}
        ampliable={false}
        titulo="Recibo"
        numero={venta.number}
        fecha={fechaDelPapel(venta.created_at)}
        emisor={{
          negocio: datosNegocio?.name ?? "",
          razonSocial: datosNegocio?.legal_name,
          logo: datosNegocio?.logo_url,
          rif: datosNegocio?.tax_id,
          // De otra sucursal, su nombre va siempre: es la mitad del encabezado.
          sucursal:
            varias || venta.sucursal_es_principal === false ? venta.sucursal_nombre : null,
          direccion,
          telefono,
          domicilioFiscal: domicilioFiscalDe(datosNegocio, venta.sucursal_es_principal),
        }}
        datos={[
          { etiqueta: "Cliente", valor: venta.customer_name ?? "—" },
          { etiqueta: esVenezuela ? "C.I./RIF" : "Documento", valor: venta.customer_documento ?? "—" },
          ...(venta.customer_telefono ? [{ etiqueta: "Teléfono", valor: venta.customer_telefono }] : []),
          // «Asesor» y no «Atendió» (lo pidió el dueño, 2026-10-01).
          ...(venta.sold_by_name ? [{ etiqueta: "Asesor", valor: venta.sold_by_name }] : []),
          ...(venta.corrige_a_numero ? [{ etiqueta: "Corrige a", valor: venta.corrige_a_numero }] : []),
        ]}
        aviso={
          venta.status === "voided" ? (
            <div
              style={{
                border: `2px solid ${ROJO}`,
                color: ROJO,
                padding: "6px 10px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                textAlign: "center",
              }}
            >
              Anulada{venta.void_reason ? ` · ${venta.void_reason}` : ""}
            </div>
          ) : undefined
        }
        columnas={[
          { titulo: "Cantidad", ancho: 76 },
          { titulo: "Descripción" },
          ...(conColumnaDeCodigo
            ? [{ titulo: codigoDeProducto === "barcode" ? "Código de barras" : "SKU", ancho: 120 }]
            : []),
          { titulo: `Precio ${monedaImpresa(codigo)}`, ancho: 100, derecha: true },
          { titulo: `Total ${monedaImpresa(codigo)}`, ancho: 108, derecha: true },
        ]}
        filas={filas}
        resumen={`Total de productos: ${venta.items.length}`}
        totales={totales}
        aclaracion={venta.was_offline ? "Venta registrada sin conexión." : undefined}
        observaciones={venta.note}
        despedida={datosNegocio?.settings?.receipt_footer || "¡Gracias por su compra!"}
      />
  );
}
